import React, { Suspense, lazy, useEffect, useState, useMemo } from "react";

import { Routes, Route, BrowserRouter } from "react-router-dom";

import { Provider as ReduxProvider } from "react-redux";

import { createTheme, ThemeProvider } from "@mui/material/styles";

import "./App.css";

import "./assets/scss/theme.scss";



import Landing from "./Pages/Landing/Landing";

import Auth from "./common/Auth";

import FullScreenSpinner from "./Components/FullScreenSpinner/FullScreenSpinner";

import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";

import DocumentTitle from "./Components/SEO/DocumentTitle";

import RouteCanonical from "./Components/SEO/RouteCanonical";

import RouteRobotsMeta from "./Components/SEO/RouteRobotsMeta";

import RouteSocialMeta from "./Components/SEO/RouteSocialMeta";

import RouteMetaDescription from "./Components/SEO/RouteMetaDescription";

import store from "./store/store";

import {

  SignUp2,

  Login,

  ForgetPassword,

  Confirm,

  EmailVerified,

  ContactUs,

  PrivacyPolicy,

  TermsAndConditions,

  ResourcesPage,

  ResourceCategory,

  Resource2,

  MainLayout,

  PublicLayout,

  OnboardingWizard,

  DashboardPage,

  UserProfilePage,

  ProjectsPage,

  OrganizationsPage,

  JobSearchPage,

  Settings,

  MyResources,

  ProjectDetails,

  JobDetails,

  JobApplicantList,

  OrganizationDetails,

  NotFound,

  AccessDenied,

} from "./lazyPages";



const ReactNotifications = lazy(() =>

  import("react-notifications-component").then((module) => ({

    default: module.ReactNotifications,

  }))

);



const theme = createTheme({

  palette: {

    primary: {

      main: "#0066B2",

    },

    secondary: {

      main: "#009E60",

    },

  },

  typography: {

    fontFamily: ["Manrope", "sans-serif"].join(","),

    fontSize: 14,

  },

});



function App() {

  const [appInitialized, setAppInitialized] = useState(false);

  const [userInfo, setUserInfo] = useState(null);



  useEffect(() => {
    const syncUserInfo = () => {
      try {
        const storedUserInfo = localStorage.getItem("userInfo");

        if (storedUserInfo) {
          setUserInfo(JSON.parse(storedUserInfo));
        } else {
          setUserInfo(null);
        }
      } catch (error) {
        setUserInfo(null);
      }
    };

    syncUserInfo();
    setAppInitialized(true);

    let lastToken = store.getState().auth?.accessToken;
    const unsubscribe = store.subscribe(() => {
      const nextToken = store.getState().auth?.accessToken;
      if (nextToken !== lastToken) {
        lastToken = nextToken;
        syncUserInfo();
      }
    });

    return unsubscribe;
  }, []);



  const id = useMemo(() => {

    return userInfo?._id || null;

  }, [userInfo]);



  useEffect(() => {
    if (!userInfo?._id) return;

    const name = [userInfo.firstName, userInfo.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    const context = {
      userId: userInfo._id,
      name: name || userInfo.email || userInfo._id,
      email: userInfo.email,
      environment: process.env.NODE_ENV === "production" ? "production" : "development",
      appVersion: process.env.REACT_APP_VERSION || "1.0.0",
    };

    let attempts = 0;
    const maxAttempts = 40;

    const applyContext = () => {
      if (window.TraceLoop?.setContext) {
        window.TraceLoop.setContext(context);
        return true;
      }
      return false;
    };

    if (applyContext()) return undefined;

    // Widget script is deferred; retry until it exposes TraceLoop
    const intervalId = setInterval(() => {
      attempts += 1;
      if (applyContext() || attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 250);

    return () => clearInterval(intervalId);
  }, [userInfo]);



  if (!appInitialized) {

    return (

      <ReduxProvider store={store}>

        <ThemeProvider theme={theme}>

          <FullScreenSpinner />

        </ThemeProvider>

      </ReduxProvider>

    );

  }



  return (

    <ErrorBoundary>

      <ReduxProvider store={store}>

        <ThemeProvider theme={theme}>

          <Suspense fallback={<FullScreenSpinner />}>

            <Auth>

              <BrowserRouter>

                <DocumentTitle />

                <RouteMetaDescription />

                <RouteCanonical />

                <RouteRobotsMeta />

                <RouteSocialMeta />

                <Suspense fallback={null}>

                  <ReactNotifications />

                </Suspense>

                <Routes>

                <Route path="/signup" element={<SignUp2 />} />

                <Route path="/signin" element={<Login />} />

                <Route path="/resetpassword" element={<ForgetPassword />} />

                <Route path="/confirm" element={<Confirm />} />

                <Route path="/email-verified" element={<EmailVerified />} />

                <Route path="/" element={<Landing />} />



                {!id && (

                  <Route element={<PublicLayout />}>

                    <Route path="/categories" element={<ResourcesPage />} />

                    <Route path="/category/:catCode" element={<ResourceCategory />} />

                    <Route path="/resource/:resCode" element={<Resource2 />} />

                  </Route>

                )}



                <Route element={<MainLayout />}>

                  <Route path="/onboarding" element={<OnboardingWizard />} />

                  <Route path="/onboarding/:onboardingId" element={<OnboardingWizard />} />

                  <Route path="/onboarding/project/:projectId" element={<OnboardingWizard />} />

                  

                  <Route path="/profile/:userId" element={<DashboardPage />} />

                  <Route path="/userprofile" element={<UserProfilePage />} />

                  <Route path="/projects" element={<ProjectsPage />} />

                  <Route

                    path="/organizations"

                    element={<OrganizationsPage />}

                  />

                  <Route path="/jobsearch" element={<JobSearchPage />} />

                  {id && <Route path="/categories" element={<ResourcesPage />} />}

                  <Route path="/settings" element={<Settings />} />

                  <Route path="/myresources" element={<MyResources />} />



                  <Route

                    path="/projectdetails/:projId"

                    element={<ProjectDetails />}

                  />

                  <Route path="/jobdetails/:jobId" element={<JobDetails />} />

                  <Route

                    path="/jobapplicantlist/:jobId"

                    element={<JobApplicantList />}

                  />

                  <Route

                    path="/organizationdetails/:orgId"

                    element={<OrganizationDetails />}

                  />

                  {id && (

                    <Route

                      path="/category/:catCode"

                      element={<ResourceCategory />}

                    />

                  )}



                  {id && <Route path="/resource/:resCode" element={<Resource2 />} />}

                </Route>



                <Route path="*" element={<NotFound />} />

                <Route path="/permission-denied" element={<AccessDenied />} />



                <Route path="/contact" element={<ContactUs />} />

                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                <Route

                  path="/terms-and-conditions"

                  element={<TermsAndConditions />}

                />

                </Routes>

              </BrowserRouter>

            </Auth>

          </Suspense>

        </ThemeProvider>

      </ReduxProvider>

    </ErrorBoundary>

  );

}



export default App;

